/**
 * Offline Expense Service
 *
 * Handles offline expense creation, ID generation, and display conversion.
 * Used by the sync hook and expense mutations for offline functionality.
 */
import type {
  CategoryPredictionSource,
  CreateExpenseDto,
  ExpenseResponseDto,
  GroupWithDashboard,
  PendingConversionFields,
  PendingExpense,
  UpdateExpenseDto,
} from '@splitfiesta/shared';

import { queryKeys } from '../../queries/queryKeys';
import type { PendingExpensePatch } from '../../stores/createOfflineStore';
import { randomUuid } from '../../utils/randomUuid';

/**
 * Metadata about *how* the category landed on the form when the user
 * submitted offline. Passed through to the offline queue so the
 * sync-time cloud upgrade can decide whether the free-tier guess
 * is worth replacing with a Claude call.
 *
 * Absent ⇒ the user picked the category manually ⇒ never upgrade.
 */
export interface OfflinePredictionMeta {
  source: CategoryPredictionSource;
  confidence: number;
}

/**
 * Display-friendly version of a pending expense
 * Compatible with ExpenseResponseDto for UI rendering
 */
export interface OfflineDisplayExpense extends Omit<
  ExpenseResponseDto,
  'id' | 'createdBy' | 'createdAt' | 'updatedAt'
> {
  id: string;
  isOffline: true;
  syncStatus: PendingExpense['syncStatus'];
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Carried through from the pending expense so lists can show a
   * "will convert on sync" hint for a foreign-currency expense queued
   * while offline. See `PendingConversionFields` in
   * `@splitfiesta/shared`.
   */
  pendingConversion?: boolean;
  targetCurrency?: string;
  /** Why the last sync attempt failed — an i18n key or a raw message. */
  lastError?: string;
}

/**
 * userId → display name / avatar lookup. `CreateExpenseDto` (and so
 * `PendingExpense`) carry only `{ userId, amount }` on their
 * payers/splits — no names. This map lets `toDisplayExpense`
 * rehydrate them so an offline expense shows its real payer instead
 * of the generic "unknown" fallback until it syncs and the server
 * echoes the names back.
 */
export type OfflineMemberInfo = Map<
  string,
  { userName?: string; profileImage?: string }
>;

class OfflineExpenseService {
  /**
   * Generate a unique local ID for offline expenses
   * Format: local_timestamp_uuid
   */
  generateLocalId(): string {
    return `local_${Date.now()}_${randomUuid()}`;
  }

  /**
   * Check if an expense ID is a local offline ID
   */
  /**
   * The store patch for a user's edit of a queued expense. The queue holds
   * a create DTO with no null variant for the conversion keys, so a key is
   * sent only when it is meant to be written — except `originalCurrency:
   * null`, the store's "drop the whole conversion" signal, which passes
   * through. The line-item trio passes through as is: its `null` is the
   * store's "drop the itemization" signal.
   */
  toPendingPatch(
    data: UpdateExpenseDto & PendingConversionFields
  ): PendingExpensePatch {
    const {
      originalCurrency,
      exchangeRate,
      receipt,
      notes,
      // A queued row has no server version to condition on.
      expectedUpdatedAt: _expectedUpdatedAt,
      ...rest
    } = data;
    return {
      ...rest,
      // Nothing was ever uploaded for a queued expense (it has no server
      // id), so a receipt clear has nothing to act on.
      ...(typeof receipt === 'string' ? { receipt } : {}),
      // The queued create has no null note; '' is its cleared note.
      ...(notes !== undefined ? { notes: notes ?? '' } : {}),
      ...(originalCurrency === null
        ? { originalCurrency: null }
        : originalCurrency != null
          ? { originalCurrency }
          : {}),
      // `null` clears a stored manual rate; absent leaves it alone.
      ...(exchangeRate !== undefined ? { exchangeRate } : {}),
    };
  }

  /**
   * A queued expense as the screens display it, with payer and split names
   * resolved from the cached roster of its own group.
   */
  toDisplayExpenseFromCache(
    pending: PendingExpense,
    user: { id?: string; name?: string } | null | undefined,
    cache: { getQueryData<T>(key: readonly unknown[]): T | undefined }
  ): ExpenseResponseDto {
    const memberInfo = this.buildMemberInfo(
      cache.getQueryData<GroupWithDashboard>(
        queryKeys.groupWithDashboard(pending.groupId)
      )
    );
    return this.toDisplayExpense(
      pending,
      user?.id ?? 'unknown',
      user?.name,
      memberInfo
    ) as unknown as ExpenseResponseDto;
  }

  isLocalId(id: string): boolean {
    return id.startsWith('local_');
  }

  /**
   * Create a pending expense from form data
   *
   * @param data        — the create-expense payload the user submitted
   * @param ownerId     — the signed-in user queuing it
   * @param predictionMeta — optional metadata about how the category
   *   landed on the form. Pass it when the category was auto-picked
   *   (keyword / on-device LLM / fallback) so the sync-time cloud
   *   upgrade can decide whether to call `predict-category` before
   *   the POST. Omit when the user picked the category manually —
   *   the absence of these fields is the signal to leave it alone.
   */
  createPendingExpense(
    data: CreateExpenseDto,
    ownerId: string,
    predictionMeta?: OfflinePredictionMeta
  ): PendingExpense {
    return {
      ...data,
      idempotencyKey: data.idempotencyKey ?? randomUuid(),
      localId: this.generateLocalId(),
      ownerId,
      syncStatus: 'pending',
      createdAt: Date.now(),
      retryCount: 0,
      ...(predictionMeta && {
        predictedCategorySource: predictionMeta.source,
        predictedCategoryConfidence: predictionMeta.confidence,
      }),
    };
  }

  /**
   * Build a userId → name/avatar lookup from a group's cached
   * `balances` roster (each entry carries `userName` + an optional
   * `profileImage`). Pass the result to `toDisplayExpense` so offline
   * rows show real payer/split names instead of "unknown".
   */
  buildMemberInfo(
    group?: Pick<GroupWithDashboard, 'balances'> | null
  ): OfflineMemberInfo {
    const map: OfflineMemberInfo = new Map();
    for (const b of group?.balances ?? []) {
      if (b.userName) {
        map.set(b.userId, {
          userName: b.userName,
          profileImage: b.profileImage ?? undefined,
        });
      }
    }
    return map;
  }

  /**
   * Convert pending expense to display format
   * Makes it compatible with ExpenseResponseDto for UI rendering
   *
   * `memberInfo` (from `buildMemberInfo`) rehydrates the payer/split
   * names the create DTO never carried. When it's absent or doesn't
   * cover a userId, we fall back to the current user's own identity
   * for their entries (the common "I paid" offline case), then to
   * undefined so the row renders its generic fallback.
   */
  toDisplayExpense(
    pending: PendingExpense,
    userId: string,
    userName?: string,
    memberInfo?: OfflineMemberInfo
  ): OfflineDisplayExpense {
    const {
      localId,
      syncStatus,
      retryCount: _retryCount,
      lastRetryAt: _lastRetryAt,
      createdAt,
      ...expenseData
    } = pending;

    const resolveMember = (
      id: string
    ): { userName?: string; profileImage?: string } => {
      const info = memberInfo?.get(id);
      if (info?.userName) return info;
      if (id === userId && userName) return { userName };
      return {};
    };

    return {
      ...expenseData,
      // Convert null subcategory to undefined for ExpenseResponseDto compatibility
      // (CreateExpenseDto uses null to clear, but response DTOs use undefined for absent values)
      subcategory: expenseData.subcategory || undefined,
      id: localId,
      isOffline: true,
      syncStatus,
      createdBy: userId,
      createdByName: userName,
      createdAt: new Date(createdAt).toISOString(),
      updatedAt: new Date(createdAt).toISOString(),
      // Rehydrate payer/split names from the group roster (the create
      // DTO only persisted userId + amount).
      payers: expenseData.payers.map(p => ({
        ...p,
        ...resolveMember(p.userId),
      })),
      splits: expenseData.splits.map(s => ({
        ...s,
        ...resolveMember(s.userId),
        isSettled: false,
      })),
    };
  }

  /**
   * Get expenses that should be retried
   * Includes pending expenses and failed expenses under max retry count
   */
  getRetryableExpenses(
    expenses: PendingExpense[],
    maxRetries = 3
  ): PendingExpense[] {
    return expenses.filter(
      e =>
        e.syncStatus === 'pending' ||
        (e.syncStatus === 'failed' && e.retryCount < maxRetries)
    );
  }

  /**
   * Sort expenses with pending offline expenses first, then by date
   */
  sortExpensesWithOfflineFirst<
    T extends { createdAt: string; isOffline?: boolean },
  >(expenses: T[]): T[] {
    return [...expenses].sort((a, b) => {
      // Pending offline expenses first
      if (a.isOffline && !b.isOffline) return -1;
      if (!a.isOffline && b.isOffline) return 1;
      // Then by date descending (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  /**
   * Merge online expenses with pending offline expenses for display
   */
  mergeWithPendingExpenses(
    onlineExpenses: ExpenseResponseDto[],
    pendingExpenses: PendingExpense[],
    userId: string,
    userName?: string
  ): (ExpenseResponseDto | OfflineDisplayExpense)[] {
    const offlineDisplayExpenses = pendingExpenses.map(p =>
      this.toDisplayExpense(p, userId, userName)
    );

    return this.sortExpensesWithOfflineFirst([
      ...offlineDisplayExpenses,
      ...onlineExpenses,
    ]);
  }

  /**
   * Calculate time since last retry attempt
   */
  getTimeSinceLastRetry(expense: PendingExpense): number | null {
    if (!expense.lastRetryAt) return null;
    return Date.now() - expense.lastRetryAt;
  }

  /**
   * Check if expense should be retried based on backoff
   * Uses exponential backoff: 5s, 10s, 20s, etc.
   */
  shouldRetryWithBackoff(expense: PendingExpense, baseDelayMs = 5000): boolean {
    if (expense.syncStatus !== 'failed') return true;
    if (!expense.lastRetryAt) return true;

    const delay = baseDelayMs * Math.pow(2, expense.retryCount - 1);
    const timeSinceLastRetry = Date.now() - expense.lastRetryAt;

    return timeSinceLastRetry >= delay;
  }
}

// Export singleton instance
export const offlineExpenseService = new OfflineExpenseService();
